/**
 * Crow’s Nest info-tip copy — sales-facing explanations for The Bridge.
 */

export const CROWS_NEST_TOOLTIPS = Object.freeze({
  ollieFindingsSection:
    'Ollie checks by how much they help: where each found something, who saw it, who snoozed it, who acted on it, whether the next check found it fixed, and how often people said "This isn\'t right". UK days; defaults to the last 30. Checks with nothing in range are folded away.',
  ollieSnoozed:
    'People who tapped "Remind me later" (ollie_finding_snoozed), and the share of people shown it. Red at 50%+ once 10+ people have seen it: the clearest sign a check is too noisy.',
  ollieActedOn:
    'People who did something with it: opened the fix (clicked), confirmed a prepared change, used a supplier draft, or marked it done. Share of people shown it.',
  ollieFound:
    'Venues where the check found something new (ollie_finding_generated, written by the 6am run).',
  ollieShown:
    'People shown the finding in the panel, a feature page or Success Hub (ollie_finding_shown). Unique people, not views.',
  ollieFixed:
    'Times the next check found some or all of it gone (ollie_finding_resolved), and at how many venues. Price rises, out-of-range readings and stock gaps can also drop out on their own, so read their fixes with care.',
  ollieTimeToFix:
    'Median days from Ollie first spotting it to the check finding it gone (daysToResolve on ollie_finding_resolved).',
  ollieReportedWrong:
    'People who tapped "This isn\'t right" (ollie_finding_reported_wrong), and the share of people shown it. "Already sorted" that the checks confirmed (the re-check found it gone, or the check resolved within an hour) is listed underneath and not counted.',
  ollieStatus:
    'Needs work: 5%+ of people shown it said it is wrong (2+ reports), or an act check found at 5+ venues and fixed at none. Helping: fixed at 3+ venues. Too early: shown to fewer than 10 people.',
  northStarSection:
    'Free venues that hit a premium feature limit and created a paywall sales opportunity. Compared to the previous 7 UK days.',
  freeVenuesAtPaywall:
    'Unique Free venues that created at least one paywall sales opportunity in the last 7 UK days.',
  paywallOpportunities:
    'Count of new paywall opportunities in that window (same venue can count more than once if a new opportunity is created after cooldown).',
  salesEmailsSent:
    'How many of those opportunities successfully emailed sales@.',
  weekOverWeekDelta: 'Change vs the previous 7 UK days.',
  funnelSection:
    'Views → clicks → opened → first success → repeat use → Free paywalls for Free venues (last 30 UK days). Spot where adoption leaks before sales opportunities.',
  views:
    'Times a Growth Engine recommendation was shown (recommendation_displayed). Free plan, last 30 UK days.',
  clicks: 'Times a recommendation CTA was clicked (recommendation_clicked).',
  opened:
    'They reached the feature screen (feature_opened) — looky-loos vs people who never opened it.',
  firstUses:
    'First real use of the feature for that venue (feature_first_used) — not the same as “accepted nudge.”',
  firstSuccess:
    'Guided Success first win (first_success_achieved) — a real save, not a click or walkthrough start.',
  repeatUse:
    'They came back after the first win (feature_used_again). Low here means one-and-done.',
  paywalls:
    'Free limit hits (free_limit_reached). Celebrate only when first success is also above zero — otherwise they can be admin impersonation or a limit hit before anyone saved.',
  ctr: 'Clicks ÷ views.',
  clickToUse: 'First uses ÷ clicks.',
  clickToOpened: 'Opened ÷ clicks.',
  openedToFirstSuccess:
    'First success ÷ opened — opened the screen but never saved.',
  firstSuccessToRepeat:
    'Repeat use ÷ first success — saved once and never came back.',
  useToPaywall: 'Paywalls ÷ first uses.',
  firstSuccessToPaywall:
    'Paywalls ÷ first success. A dash or a huge ratio with 0 first success is not a healthy funnel.',
  leakHint: 'Quick read on where this feature is leaking in the funnel.',
  ladderCensusSection:
    'How many Free venues sit at each ladder band right now — 0–2 suppliers vs 3+, dormant, or still unadopted on recipes / stock / par. The missing “6 of how many.”',
  censusVenues:
    'Free venues in this snapshot. Missing UsageLimits counts as Free. This is the denominator for every “X of Y” row.',
  censusBelowMilestone:
    'Active suppliers below the first milestone (3). Same active-supplier definition as the Growth Engine ladder.',
  censusAtOrAboveMilestone:
    'Active suppliers at or above the first milestone (3).',
  censusDormant: 'No order in the last 90 days — same window as Activate.',
  censusUnadoptedRecipes:
    '0 saved recipes. Product evidence, not a recommendation click.',
  censusUnadoptedStockTakes:
    '0 stock takes. Product evidence, not a recommendation click.',
  censusUnadoptedParLevels:
    '0 products with par > 0. Product evidence, not a recommendation click.',
  censusUnadoptedAll:
    '0 recipes, 0 stock takes, and 0 pars — still untouched on all three.',
  timeToConversionSection:
    'Median days from opening a feature to first success, and from first success to a Free paywall. Empty data stays “—” — a lone first-use is not a duration.',
  timeToFirstSuccess:
    'Median days from earliest feature_opened to first_success_achieved (falls back to feature_first_used for Phase 1 history). No opened event → not in the sample.',
  timeToPaywall:
    'Median days from first win (first_success_achieved, else feature_first_used) to free_limit_reached.',
  opportunityFeed:
    'Recent sales signals. Counts split paywalls vs supplier adds — “11 leads” is not 11 paywalls. Default last 30 UK days. New toast + confetti while you’re on this page (once per opportunity).',
  opportunityMix:
    'Unfiltered mix for this date range. Filtering the table by type does not change these totals, so a paywall-only page still shows how many supplier adds sit beside it.',
  unadoptedSection:
    'Venues that ordered in the last 90 days but never adopted recipes, stock takes, or par levels. Includes paid — the ladder census “6 of how many” stays Free-only.',
  unadoptedVenues:
    'How many venues are on this list right now (current filters). Dormant venues and fully adopted venues stay off it.',
  unadoptedPaid:
    'Paid venues (Starter / Growth / Pro) with a depth gap. Phase 1 hid these because they cannot hit a Free paywall.',
  unadoptedFree:
    'Free venues on the same interview list — they order, but have never saved recipes, stock, or pars.',
  signal: 'What triggered the row (e.g. Paywall / Par levels).',
  status:
    'Open = nobody has marked a reach-out yet. Contacted = a teammate touched this lead (sets Last touched by).',
  owner:
    'Who last marked contacted or saved a note on this opportunity — so the team does not double-dial.',
  salesNote:
    'Optional one-liner for the team (e.g. “Left voicemail Monday”). Max 280 characters.',
  whyFired:
    'What triggered this sales signal (usage vs limit, product tried, supplier add, etc.).',
  ageBadge:
    'How fresh the lead is in UK time. Same-day leads are highlighted — contact them today.',
  actions:
    'Create a Catch Board deal from this lead, copy a prefilled email draft (To / Subject / body), or copy venue + context for Slack / HubSpot.',
  handoffFilter:
    'All leads (default), Open, Contacted, or Mine (leads you last touched).',
  cooldownHint:
    'Opportunity writers dedupe for 24 hours per venue + feature. Explains why the same paywall may not create another feed row yet.',
  suggestedFollowUp: 'One-liner for outreach, from the feature registry.',
  salesEmail:
    'Whether the automated sales@ email was sent for this opportunity.',
  typeFilter:
    'Filter by signal type. Same venue can appear again after the 24h cooldown, or sooner for a different feature.',
})

export const CROWS_NEST_NORTH_STAR_METRIC_TIPS = Object.freeze({
  uniqueVenues: CROWS_NEST_TOOLTIPS.freeVenuesAtPaywall,
  opportunities: CROWS_NEST_TOOLTIPS.paywallOpportunities,
  emailsSent: CROWS_NEST_TOOLTIPS.salesEmailsSent,
})

export const CROWS_NEST_CENSUS_ROW_TIPS = Object.freeze({
  belowMilestone: CROWS_NEST_TOOLTIPS.censusBelowMilestone,
  atOrAboveMilestone: CROWS_NEST_TOOLTIPS.censusAtOrAboveMilestone,
  dormant: CROWS_NEST_TOOLTIPS.censusDormant,
  unadoptedRecipes: CROWS_NEST_TOOLTIPS.censusUnadoptedRecipes,
  unadoptedStockTakes: CROWS_NEST_TOOLTIPS.censusUnadoptedStockTakes,
  unadoptedParLevels: CROWS_NEST_TOOLTIPS.censusUnadoptedParLevels,
  unadoptedRecipesStockAndPar: CROWS_NEST_TOOLTIPS.censusUnadoptedAll,
})

export const CROWS_NEST_FUNNEL_STAGE_TIPS = Object.freeze({
  views: CROWS_NEST_TOOLTIPS.views,
  clicks: CROWS_NEST_TOOLTIPS.clicks,
  opened: CROWS_NEST_TOOLTIPS.opened,
  firstUses: CROWS_NEST_TOOLTIPS.firstUses,
  firstSuccess: CROWS_NEST_TOOLTIPS.firstSuccess,
  repeatUse: CROWS_NEST_TOOLTIPS.repeatUse,
  paywalls: CROWS_NEST_TOOLTIPS.paywalls,
})
